// src/lib/api.js - Centralized API helper for CRA
const API_BASE = (process.env.REACT_APP_API_URL || "https://api.go2cdl.com").replace(/\/+$/, "");

// Debug logging
console.log("🔍 API Debug Info:");
console.log("  REACT_APP_API_URL:", process.env.REACT_APP_API_URL);
console.log("  API_BASE:", API_BASE);
console.log("  Current URL:", window.location.href);
console.log("  Timestamp:", new Date().toISOString());

export async function api(path, opts = {}) {
  const headers = { 
    ...(opts.headers || {}) 
  };
  
  // Only set Content-Type if not FormData (browser needs to set multipart boundary)
  if (!(opts.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  
  // Add Authorization header if token exists
  const token = localStorage.getItem('accessToken');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const url = `${API_BASE}${path}`;
  console.log(`🌐 API Request: ${opts.method || 'GET'} ${url}`);
  
  try {
    const res = await fetch(url, { 
      ...opts, 
      headers, 
      credentials: 'include' 
    });
    
    console.log(`📡 API Response: ${res.status} ${res.statusText}`);
    
    if (!res.ok) {
      let errorMessage = `HTTP ${res.status}`;
      const contentType = res.headers.get('content-type') || '';
      try {
        if (contentType.includes('application/json')) {
          const data = await res.json();
          errorMessage = data.error || data.message || JSON.stringify(data);
        } else {
          const text = await res.text();
          errorMessage = text || errorMessage;
        }
      } catch (parseError) {
        console.error('Failed to parse error response:', parseError);
      }
      console.error(`❌ API Error: ${res.status} - ${errorMessage}`);
      throw new Error(errorMessage);
    }

    return res.json();
  } catch (error) {
    console.error(`💥 API Fetch Error:`, error);
    throw error;
  }
}

// Convenience methods
export const auth = {
  login: async (email, password) => {
    const response = await api('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    // Extract user data and persist token
    const { user, accessToken } = response;
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
    }
    
    return { user, accessToken }; // Return both user and token
  },
  
  signup: async (firstName, lastName, phone, email, password, registrationCode, referralCode = '') => {
    return api('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ firstName, lastName, phone, email, password, registrationCode, referralCode }),
    });
  },
  
  logout: () => api('/auth/logout', { method: 'POST' }),
  
  me: () => api('/auth/me'),
  
  validateCode: (code) => api('/auth/validate-code', {
    method: 'POST',
    body: JSON.stringify({ code }),
  }),
};

export const exams = {
  getCategories: () => api('/api/exams/categories'),
  getQuestions: (category) => api(`/api/exams/questions/${encodeURIComponent(category)}`),
  submitAnswer: (questionId, answer) => api('/api/exams/answer', {
    method: 'POST',
    body: JSON.stringify({ questionId, answer }),
  }),
  updateQuestion: (questionId, data) => api(`/api/exams/questions/${questionId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

export const documents = {
  getMy: () => api('/api/documents/my'),
  upload: (formData) => api('/api/documents/upload', {
    method: 'POST',
    body: formData,
    headers: {}, // Let browser set Content-Type for FormData
  }),
  delete: (documentId) => api(`/api/documents/${documentId}`, { method: 'DELETE' }),
  download: (docId) => `${API_BASE}/api/documents/${docId}/download`,
};

export const enrollment = {
  get: () => api('/api/enrollment'),
  submit: (data) => api('/api/enrollment/submit', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateStatus: (enrollmentId, status) => api(`/api/enrollment/${enrollmentId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  }),
  delete: (enrollmentId) => api(`/api/enrollment/${enrollmentId}`, { method: 'DELETE' }),
};

export const completion = {
  getStats: () => api('/api/completion/completion-stats'),
  markCompleted: (userId) => api(`/api/completion/mark-completed/${userId}`, { method: 'POST' }),
  getCompletedStudents: () => api('/api/completion/completed-students'),
};

export const trainingProgress = {
  getUserProgress: (userId) => api(`/api/training-progress/user/${userId}`),
  updateProgress: (userId, data) => api(`/api/training-progress/user/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  getSummary: () => api('/api/training-progress/summary'),
  getAll: () => api('/api/training-progress/all'),
};

export const studyMaterials = {
  get: () => api('/api/study-materials'),
  upload: (formData) => api('/api/study-materials/upload', {
    method: 'POST',
    body: formData,
    headers: {}, // Let browser set Content-Type for FormData
  }),
  delete: (materialId) => api(`/api/study-materials/${materialId}`, { method: 'DELETE' }),
};

export const training = {
  getTrucks: () => api('/api/training/trucks'),
  getSessions: (type, weekStart) => api(`/api/training/sessions?type=${encodeURIComponent(type)}&weekStart=${encodeURIComponent(weekStart)}`),
  bookSession: (payload) => api('/api/training/sessions', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  cancelSession: (sessionId) => api(`/api/training/sessions/${sessionId}/cancel`, {
    method: 'PATCH',
  }),
};

export const appointments = {
  getTrucks: () => api('/api/appointments/trucks'),
};

export const payments = {
  getStudentSummary: () => api('/api/payments/summary'),
  generateReferralCode: () => api('/api/payments/referral', { method: 'POST' }),
  applyReferralCode: (code) => api('/api/payments/apply-referral', {
    method: 'POST',
    body: JSON.stringify({ code }),
  }),
  submitPayment: ({ amount, method, note, receipt }) => {
    const formData = new FormData();
    formData.append('amount', amount);
    formData.append('method', method);
    if (note) {
      formData.append('note', note);
    }
    if (receipt) {
      formData.append('receipt', receipt);
    }

    return api('/api/payments', {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },
};

export const adminPayments = {
  getOverview: () => api('/api/admin/payments'),
  updateSettings: (data) => api('/api/admin/payments/settings', {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  updateStudentTuition: (studentId, totalDue) => api(`/api/admin/payments/student/${studentId}/tuition`, {
    method: 'PATCH',
    body: JSON.stringify({ totalDue }),
  }),
  updatePaymentStatus: (paymentId, status) => api(`/api/admin/payments/${paymentId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  }),
  deletePayment: (paymentId) => api(`/api/admin/payments/${paymentId}`, {
    method: 'DELETE',
  }),
  revokeAppliedDiscount: (studentId) => api(`/api/admin/payments/student/${studentId}/revoke-discount`, {
    method: 'POST',
  }),
  invalidateReferralCode: (studentId) => api(`/api/admin/payments/student/${studentId}/invalidate-code`, {
    method: 'POST',
  }),
  approveReferralDiscount: (studentId) => api(`/api/admin/payments/student/${studentId}/approve-referral`, {
    method: 'POST',
  }),
  deleteStudentRecord: (studentId) => api(`/api/admin/payments/student/${studentId}`, {
    method: 'DELETE',
  }),
};

// Health check function
export const getHealth = () => fetch(`${API_BASE}/health`).then(r => r.json());

// Export the base URL for cases where we need the full URL
export { API_BASE };
